import { InputGroup } from '@blueprintjs/core';
import styled from '@emotion/styled';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import internal from 'assert';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import React, { useCallback } from 'react';
import { TLCode } from '../../common/enums';
import { LocaleStore, MunicipalityStore, ProtectionStore, TransferRequestStore, PharmacyStore } from '../../store';
import { Municipality } from '../../store/models/Municipality';
import BaseGrid from '../common/grid/BaseGrid';
import EditCell from '../common/grid/EditCell';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import {
  renderGridDate
} from '../../store/utils';

interface RequestListProps extends WithTranslateProps {
  PharmacyStore?: Instance<typeof PharmacyStore>;
  ProtectionStore?: Instance<typeof ProtectionStore>;
  TransferRequestStore?: Instance<typeof TransferRequestStore>;
  MunicipalityStore?: Instance<typeof MunicipalityStore>;
  LocaleStore?: Instance<typeof LocaleStore>;
}

interface RequestListState {
  isLoading: boolean;
  municipalityToEdit?: Instance<typeof Municipality>;
  filter: string;
}

interface TransferRequestToDisplay {
  id: number;
  // pharmacyId: number;
  apbNumber: number;
  requestDate: string;
  decisionDate: string;
  decision: string;
  firstPublishDate: string;
  firstPublishDateAfterDecision: string;
  initialAddress: string,
  initialZipCode: string,
  initialCity: string,
  newAddress: string,
  newZipCode: string,
  newCity: string,
  procedure: string;
  chamber: string;
  startDate: string;
  endDate: string;
  duration: string;
  //durationUom: string;
  range: string;
  //rangeUom: string;
}

@inject('ProtectionStore', 'TransferRequestStore', 'PharmacyStore', 'LocaleStore')
@observer
class RequestList extends React.Component<RequestListProps, RequestListState> {
  state = {
    isLoading: false,
    municipalityToEdit: undefined,
    filter: '',
  } as RequestListState;

  async componentDidMount() {
    const { ProtectionStore, TransferRequestStore } = this.props;
    this.setState({ isLoading: true });
    // await ProtectionStore!.checkListLoaded(); // loadList();
    await TransferRequestStore!.checkListLoaded();;
    this.setState({ isLoading: false });
  }

  columnsDefinition = [
    // {
    //   headerName: this.props.LocaleStore!.getText(TLCode.MUNICIPALITY_Id),
    //   width: 60,
    //   field: 'id',
    // },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.PHARMACIES_ApbNum),
      width: 50,
      field: 'apbNumber',
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.PROTECTION_Type),
      width: 50,
      field: 'procedure',
    },    {
      headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_requestDate),
      width: 80,
      sort: 'desc',
      field: 'requestDate',
      cellRenderer: renderGridDate
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_decisionDate),
      width: 70,
      field: 'decisionDate',
      cellRenderer: renderGridDate
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_decision),
      width: 60,
      field: 'decision',
    },
    // {
    //   headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_initialAddress),
    //   field: 'initialAddress',
    //   width: 200
    // },
    // {
    //   headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_initialZipCode),
    //   field: 'initialZipCodes',
    //   width: 60
    // },
    // {
    //   headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_initialCity),
    //   field: 'initialCity',
    //   width: 100
    // },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_newAddress),
      field: 'newAddress',
      width: 200
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_newZipCode),
      field: 'newZipCodes',
      width: 60
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_newCity),
      field: 'newCity',
      width: 100
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_firstPublishDate),
      width: 70,
      field: 'firstPublishDate',
      cellRenderer: renderGridDate
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_firstPublishAfterDecisionDate),
      width: 70,
      field: 'firstPublishDateAfterDecision',
      cellRenderer: renderGridDate
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.PROTECTION_StartDate),
      field: 'startDate',
      width: 70,
      cellRenderer: renderGridDate
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.PROTECTION_EndDate),
      field: 'endDate',
      width: 70,
      cellRenderer: renderGridDate
    },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.PROTECTION_Duration),
      field: 'duration',
      width: 60
    },
    // {
    //   headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_newZipCode),
    //   field: 'durationUom',
    //   width: 60
    // },
    {
      headerName: this.props.LocaleStore!.getText(TLCode.PROTECTION_Range),
      field: 'range',
      width: 60
    },
    // {
    //   headerName: this.props.LocaleStore!.getText(TLCode.TRANSFER_REQUEST_newZipCode),
    //   field: 'rangeUom',
    //   width: 60
    // },
    ] as (ColDef | ColGroupDef)[];

  buildGridData = () => {
    return this.props.TransferRequestStore!.dataList.map((m) => {
      // var ph = this.props.PharmacyStore!.getById(m.pharmacyId!);
      return {
        id: m.transferRequestId,
        apbNumber: m.apbNumber,
        requestDate: m.requestDate,
        decisionDate: m.decisionDate.startsWith("0001")?'':m.decisionDate,
        decision: m.decision=='+'?'accepté':(m.decision=='-'?'rejeté':''),
        firstPublishDate: m.firstPublishDate,
        firstPublishDateAfterDecision: m.firstPublishDateAfterDecision,
        initialAddress: m.initialAddress,
        initialZipCode: m.initialZipCode,
        initialCity: m.initialCity,
        newAddress: m.newAddress,
        newZipCode: m.newZipCode,
        newCity: m.newCity,
        procedure: m.procedure,
        chamber: m.chamber,
        startDate: m.protectionStartDate,
        endDate: m.protectionEndDate,
        // duration: m.protectionDuration,
        // durationUom: m.protectionDurationUom,
        // range: m.protectionRange,
        // rangeUom: m.protectionRangeUom,
        duration: m.protectionDuration ? String(m.protectionDuration) + m.protectionDurationUom : 'n/a',
        range: m.protectionRange ? String(m.protectionRange) + m.protectionRangeUom : 'n/a'
      } as TransferRequestToDisplay;
    });
    // return this.props.ProtectionStore!.dataList.map((m) => {
    //   var ph = this.props.PharmacyStore!.getById(m.pharmacyId!);
    //   return {
    //     id: m.protectionId,
    //     pharmacyId: m.pharmacyId,
    //     apbNumber: ph ? ph!.apbNumber : '',
    //     name: ph ? ph!.name : '',
    //     status: m.status ? m.status : '',
    //     startDate: m.startDate,
    //     endDate: m.endDate,
    //     duration: m.duration ? String(m.duration) + m.durationUom : 'n/a',
    //     range: m.range ? String(m.range) + m.rangeUom : 'n/a',
    //     type: m.type,
    //   } as ProtectionToDisplay;
    // });
  };

  render() {
    return (
      <Container>
        <DivLine>
          <DivLeft>
            <InputGroup
              placeholder="Filter"
              leftIcon="search"
              value={this.state.filter}
              onChange={(e) => this.setState({ filter: e.target.value })}
            />
          </DivLeft>
        </DivLine>
        <BaseGrid
          keyName="id"
          defaultColDef={{
            filter: false,
            sortable: true,
          }}
          columnDefs={this.columnsDefinition}
          rowData={this.buildGridData()}
          loading={this.state.isLoading}
          quickFilterText={this.state.filter}
        />
      </Container>
    );
  }
}

export default withTranslate(RequestList);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
